import {
  classifyRenderer,
  mergeSlowness,
  parseModeOverride,
  parseRenderMemory,
  type FlowThemeModeOverride,
  type RendererClass,
  type RendererProbeResult,
  type SessionRenderMemory,
  type SessionSlowness,
} from './renderBudget';

/**
 * Browser side of the render policy in `renderBudget.ts`: the one-off
 * renderer probe, the QA override and this tab's session memory. No React, so
 * the canvas host can use it directly.
 */

/** Query parameter that forces a mode for QA and demos: `still`, `animated`, `software` or `auto` (clear). */
export const MODE_OVERRIDE_PARAM = 'flowThemeMode';
/**
 * Storage key for the same override. In localStorage it persists across
 * sessions (set it from the console); the query parameter is copied into
 * sessionStorage so it survives in-app navigation for the rest of the tab.
 */
export const MODE_OVERRIDE_STORAGE_KEY = 'wf.flowThemeMode';
/** sessionStorage key for what this tab learned (pixel budget, slow device). */
export const RENDER_MEMORY_KEY = 'wf.flowTheme.renderBudget';

const safeStorage = (kind: 'local' | 'session'): Storage | null => {
  try {
    if (typeof window === 'undefined') {
      return null;
    }
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
};

const storageGet = (kind: 'local' | 'session', key: string): string | null => {
  try {
    return safeStorage(kind)?.getItem(key) ?? null;
  } catch {
    return null;
  }
};

const storageSet = (kind: 'local' | 'session', key: string, value: string | null) => {
  try {
    const storage = safeStorage(kind);
    if (value === null) {
      storage?.removeItem(key);
    } else {
      storage?.setItem(key, value);
    }
  } catch {
    // Private modes and full quotas: the module-level copy still covers this page load.
  }
};

// ---------------------------------------------------------------------------
// Renderer probe

const releaseContext = (gl: WebGLRenderingContext | null) => {
  try {
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch {
    // Nothing to release.
  }
};

const readRenderer = (gl: WebGLRenderingContext | null): string | null => {
  if (!gl || typeof gl.getParameter !== 'function') {
    return null;
  }
  try {
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer = gl.getParameter(info ? info.UNMASKED_RENDERER_WEBGL : gl.RENDERER);
    return typeof renderer === 'string' ? renderer : null;
  } catch {
    return null;
  }
};

const isWebGLContext = (value: unknown): value is WebGLRenderingContext =>
  Boolean(value) && typeof (value as WebGLRenderingContext).getParameter === 'function';

/** Runs the WebGL probe. Exported for tests; the app goes through `getRendererClass`. */
export const probeRenderer = (): RendererProbeResult => {
  const result: RendererProbeResult = { webglSupported: false, performantContext: false, anyContext: false, renderer: null };
  if (typeof document === 'undefined' || typeof window === 'undefined' || !('WebGLRenderingContext' in window)) {
    return result;
  }
  result.webglSupported = true;
  try {
    // Two throwaway canvases: a canvas keeps the first context type it was asked for.
    const performant = document.createElement('canvas').getContext('webgl', { failIfMajorPerformanceCaveat: true });
    if (isWebGLContext(performant)) {
      result.performantContext = true;
      result.anyContext = true;
      result.renderer = readRenderer(performant);
      releaseContext(performant);
      return result;
    }
    const fallback = document.createElement('canvas').getContext('webgl');
    if (isWebGLContext(fallback)) {
      result.anyContext = true;
      result.renderer = readRenderer(fallback);
      releaseContext(fallback);
    }
  } catch {
    // A throwing probe tells us nothing: leave it to the runtime fallback.
  }
  return result;
};

let rendererClassCache: RendererClass | null = null;

/** The renderer class, probed once per page load (a few ms, on the first themed screen). */
export const getRendererClass = (): RendererClass => {
  if (rendererClassCache === null) {
    rendererClassCache = classifyRenderer(probeRenderer());
  }
  return rendererClassCache;
};

// ---------------------------------------------------------------------------
// QA override

/**
 * The forced mode, if any. `?flowThemeMode=still|animated|software` wins and
 * is kept for the rest of the tab; `?flowThemeMode=auto` clears the stored
 * override and this tab's learned memory. Otherwise sessionStorage, then
 * localStorage. It only changes how this browser draws the backdrop, so it is
 * harmless in production.
 */
export const readModeOverride = (): FlowThemeModeOverride | null => {
  let fromQuery: ReturnType<typeof parseModeOverride> = null;
  try {
    fromQuery = parseModeOverride(new URLSearchParams(window.location.search).get(MODE_OVERRIDE_PARAM));
  } catch {
    fromQuery = null;
  }
  if (fromQuery === 'auto') {
    storageSet('session', MODE_OVERRIDE_STORAGE_KEY, null);
    storageSet('local', MODE_OVERRIDE_STORAGE_KEY, null);
    clearRenderMemory();
    return null;
  }
  if (fromQuery) {
    storageSet('session', MODE_OVERRIDE_STORAGE_KEY, fromQuery);
    return fromQuery;
  }
  for (const kind of ['session', 'local'] as const) {
    const stored = parseModeOverride(storageGet(kind, MODE_OVERRIDE_STORAGE_KEY));
    if (stored && stored !== 'auto') {
      return stored;
    }
  }
  return null;
};

// ---------------------------------------------------------------------------
// Session memory

let memoryCache: SessionRenderMemory | null = null;

export const readRenderMemory = (): SessionRenderMemory => {
  if (memoryCache === null) {
    memoryCache = parseRenderMemory(storageGet('session', RENDER_MEMORY_KEY));
  }
  return { ...memoryCache };
};

const writeRenderMemory = (next: SessionRenderMemory) => {
  memoryCache = next;
  storageSet('session', RENDER_MEMORY_KEY, JSON.stringify(next));
};

/** Remembers the backing-store budget a canvas settled on, so the next scene starts there. */
export const rememberPixelBudget = (pixelBudget: number) => {
  if (Number.isFinite(pixelBudget) && pixelBudget > 0) {
    writeRenderMemory({ ...readRenderMemory(), pixelBudget: Math.round(pixelBudget) });
  }
};

/** Remembers that this tab can't animate (some) themes, so later screens start still. */
export const rememberSlowDevice = (slowness: SessionSlowness) => {
  const memory = readRenderMemory();
  writeRenderMemory({ ...memory, slow: mergeSlowness(memory.slow, slowness) });
};

export const clearRenderMemory = () => {
  memoryCache = {};
  storageSet('session', RENDER_MEMORY_KEY, null);
};

/** Test helper: forget the probe and the in-memory session copy. */
export const resetRenderPolicyForTests = () => {
  rendererClassCache = null;
  memoryCache = null;
};
